// src/components/ListView.js

import React, { useEffect, useState } from 'react';
import api from '../api';
import {
  Typography,
  Button,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Skeleton,
  Paper,
  IconButton,
  Box,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadingState from './LoadingState';
import PageHeader from './PageHeader';

function ListView() {
  const [lists, setLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listToDelete, setListToDelete] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const response = await api.get('/lists?includeItems=false');
      if (response.status === 204) {
        setLists([]);
      } else {
        setLists(response.data);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
      setLists([]);
    } finally {
      setLoading(false);
    }
  };


  const handleCreate = async () => {
    try {
      const response = await api.post('/lists', { name: newListName });
      const newListId = response.data._id;

      setNewListName('');
      setOpen(false);
      fetchLists();
      // Navigate to the new list's page
      navigate(`/lists/${newListId}`);
    } catch (error) {
      console.error('Error creating list:', error);
    }
  };

  const handleDeleteClick = (list, event) => {
    event.stopPropagation();
    setListToDelete(list);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!listToDelete) return;

    try {
      await api.delete(`/lists/${listToDelete._id}`);
      setDeleteDialogOpen(false);
      setListToDelete(null);
      fetchLists();
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  };

  return (
    <div>
      <PageHeader
        title="My Lists"
        variant="h4"
        mb={3}
        action={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={loading}
          >
            Create New List
          </Button>
        }
      />
      {loading ? (
        <LoadingState message="Loading your lists..." minHeight={240}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={64} sx={{ mb: 1 }} />
          ))}
        </LoadingState>
      ) : (
        <>
          {/* Owned Lists */}
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontFamily: '"Fraunces", Georgia, serif' }}
            >
              My Lists
            </Typography>
            <List>
              {Array.isArray(lists) && lists.filter(list => list.isOwner).length > 0 ? (
                lists.filter(list => list.isOwner).map((list) => (
                  <Paper
                    key={list._id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(232,137,26,0.04)',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => navigate(`/lists/${list._id}`)}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {list.name}
                      </Typography>

                      <Box display="flex" alignItems="center" gap={1}>
                        {list.sharedWith && list.sharedWith.length > 0 && (
                          isSmallScreen ? (
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                fontSize: '0.9rem',
                              }}
                              title={`${list.sharedWith.length} people shared`}
                            >
                              {list.sharedWith.length}
                            </Avatar>
                          ) : (
                            <Box display="flex" gap={0.5} alignItems="center">
                              {list.sharedWith.slice(0, 2).map((share, index) => (
                                <Chip
                                  key={index}
                                  label={share.kind === 'User' ? share.item.email : share.item.name}
                                  size="small"
                                  variant="outlined"
                                  color={share.kind === 'User' ? 'secondary' : 'primary'}
                                />
                              ))}
                              {list.sharedWith.length > 2 && (
                                <Chip
                                  label={`+ ${list.sharedWith.length - 2} more`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          )
                        )}

                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(event) => handleDeleteClick(list, event)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  No lists created yet.
                </Typography>
              )}
            </List>
          </Box>

          {/* Shared Lists */}
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontFamily: '"Fraunces", Georgia, serif' }}
            >
              Shared with Me
            </Typography>
            <List>
              {Array.isArray(lists) && lists.filter(list => !list.isOwner).length > 0 ? (
                lists.filter(list => !list.isOwner).map((list) => (
                  <Paper
                    key={list._id}
                    sx={{
                      p: 2,
                      mb: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 3,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(232,137,26,0.04)',
                        transform: 'translateX(4px)',
                      },
                    }}
                    onClick={() => navigate(`/lists/${list._id}`)}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {list.name}
                      </Typography>

                      {list.owner && (
                        <Chip
                          label={`Owner: ${list.owner.email}`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                    </Box>
                  </Paper>
                ))
              ) : (
                <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  No lists shared with you.
                </Typography>
              )}
            </List>
          </Box>
        </>
      )}

      {/* Dialog for creating a new list */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create New List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            type="text"
            fullWidth
            value={newListName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleCreate();
              }
            }}
            onChange={(e) => setNewListName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete List?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{listToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ListView;
